(function () {
  var PAGE_SELECTOR = "[data-what-else-page]";
  var HERO_STAGE_SELECTOR = "[data-hero-stage]";
  var HERO_MEDIA_SELECTOR = "[data-hero-media]";
  var EXPLORER_ITEM_SELECTOR = "[data-explorer-item]";
  var EXPLORER_VISUAL_SELECTOR = "[data-explorer-visual]";
  var WHEAT_MARKER_SELECTOR = "[data-wheat-marker]";
  var SERIES_TRACK_SELECTOR = "[data-series-track]";
  var SERIES_PREVIOUS_SELECTOR = "[data-series-prev]";
  var SERIES_NEXT_SELECTOR = "[data-series-next]";
  var FUTURE_CARD_SELECTOR = "[data-future-card]";
  var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  var MOBILE_QUERY = "(max-width: 1023px)";
  var SCROLL_END_TOLERANCE = 2;
  var INITIALIZED_ATTRIBUTE = "data-what-else-initialized";
  var CLEANUP_PROPERTY = "__whatElseCanItBeCleanup";
  var EDITOR_EVENTS_GUARD = "__whatElseCanItBeEditorEventsBound";

  if (typeof window === "undefined" || typeof document === "undefined") return;

  function asArray(elements) {
    return Array.prototype.slice.call(elements);
  }

  function getMediaQuery(query) {
    return typeof window.matchMedia === "function"
      ? window.matchMedia(query)
      : null;
  }

  function addMediaQueryListener(mediaQuery, listener) {
    if (!mediaQuery) return function () {};

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", listener);
      return function () {
        mediaQuery.removeEventListener("change", listener);
      };
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(listener);
      return function () {
        mediaQuery.removeListener(listener);
      };
    }

    return function () {};
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function isInViewport(element) {
    var rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function setSelectedState(element, isActive) {
    element.classList.toggle("is-active", isActive);
    element.setAttribute("aria-selected", String(isActive));
    element.setAttribute("tabindex", isActive ? "0" : "-1");
  }

  function setExpandedState(element, isActive) {
    element.classList.toggle("is-active", isActive);

    asArray(element.querySelectorAll("[aria-expanded]")).forEach(
      function (control) {
        control.setAttribute("aria-expanded", String(isActive));
      },
    );
  }

  function pauseVideos(videos) {
    videos.forEach(function (video) {
      video.pause();
    });
  }

  function playVideos(videos, stage, prefersReducedMotion) {
    if (
      !stage ||
      document.hidden ||
      (prefersReducedMotion && prefersReducedMotion.matches) ||
      !isInViewport(stage)
    ) {
      return;
    }

    videos.forEach(function (video) {
      var playPromise = video.play();

      // 自动播放可能被浏览器策略拦截，避免产生未捕获的 Promise 异常。
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(function () {});
      }
    });
  }

  function initializeHero(page) {
    var stage = page.querySelector(HERO_STAGE_SELECTOR);
    var media = page.querySelector(HERO_MEDIA_SELECTOR);
    var prefersReducedMotion = getMediaQuery(REDUCED_MOTION_QUERY);
    var mobileQuery = getMediaQuery(MOBILE_QUERY);
    var animationFrameId = null;

    if (!stage) return function () {};

    var videos = media
      ? media.matches("video")
        ? [media]
        : asArray(media.querySelectorAll("video"))
      : [];

    function syncVideoSources() {
      var useMobileSource = mobileQuery && mobileQuery.matches;

      videos.forEach(function (video) {
        var nextSource = useMobileSource
          ? video.dataset.mobileSrc
          : video.dataset.desktopSrc;

        if (!nextSource || video.getAttribute("src") === nextSource) return;

        video.pause();
        video.setAttribute("src", nextSource);
        video.load();
      });

      playVideos(videos, stage, prefersReducedMotion);
    }

    function updateProgress() {
      animationFrameId = null;

      var baseWidth = mobileQuery && mobileQuery.matches ? 195 : 537;
      var baseHeight = mobileQuery && mobileQuery.matches ? 260 : 400;
      var finalScale = Math.max(
        window.innerWidth / baseWidth,
        window.innerHeight / baseHeight,
      );

      if (prefersReducedMotion && prefersReducedMotion.matches) {
        stage.style.setProperty("--hero-scale", finalScale.toFixed(4));
        return;
      }

      var rect = stage.getBoundingClientRect();
      var scrollRange = Math.max(rect.height - window.innerHeight, 0);
      var progress = scrollRange > 0 ? clamp(-rect.top / scrollRange, 0, 1) : 1;
      var scale = 1 + (finalScale - 1) * progress;

      stage.style.setProperty("--hero-scale", scale.toFixed(4));
    }

    function requestProgressUpdate() {
      if (animationFrameId !== null) return;

      animationFrameId = window.requestAnimationFrame(updateProgress);
    }

    function handleMotionPreferenceChange() {
      updateProgress();

      if (prefersReducedMotion && prefersReducedMotion.matches) {
        pauseVideos(videos);
        return;
      }

      playVideos(videos, stage, prefersReducedMotion);
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        pauseVideos(videos);
        return;
      }

      playVideos(videos, stage, prefersReducedMotion);
    }

    window.addEventListener("scroll", requestProgressUpdate, { passive: true });
    window.addEventListener("resize", requestProgressUpdate);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    var removeMotionListener = addMediaQueryListener(
      prefersReducedMotion,
      handleMotionPreferenceChange,
    );
    var removeMobileListener = addMediaQueryListener(
      mobileQuery,
      syncVideoSources,
    );

    syncVideoSources();
    updateProgress();

    return function () {
      window.removeEventListener("scroll", requestProgressUpdate);
      window.removeEventListener("resize", requestProgressUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      removeMotionListener();
      removeMobileListener();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      pauseVideos(videos);
    };
  }

  function initializeExplorer(page) {
    var items = asArray(page.querySelectorAll(EXPLORER_ITEM_SELECTOR));
    var visuals = asArray(page.querySelectorAll(EXPLORER_VISUAL_SELECTOR));

    if (!items.length) return;

    function setActiveItem(index) {
      if (typeof index === "undefined") return;

      items.forEach(function (item) {
        setSelectedState(item, item.dataset.index === index);
      });

      visuals.forEach(function (visual) {
        var isActive = visual.dataset.index === index;
        visual.classList.toggle("is-active", isActive);
        visual.hidden = !isActive;
        visual.setAttribute("aria-hidden", String(!isActive));
      });
    }

    function selectByPosition(position) {
      var item = items[position];
      if (item) setActiveItem(item.dataset.index);
    }

    var activeItem = items.filter(function (item) {
      return (
        item.classList.contains("is-active") ||
        item.getAttribute("aria-selected") === "true"
      );
    })[0];

    setActiveItem((activeItem || items[0]).dataset.index);

    items.forEach(function (item, position) {
      item.addEventListener("pointerenter", function (event) {
        if (event.pointerType !== "touch") setActiveItem(item.dataset.index);
      });
      item.addEventListener("focusin", function () {
        setActiveItem(item.dataset.index);
      });
      item.addEventListener("click", function () {
        setActiveItem(item.dataset.index);
      });
      item.addEventListener("keydown", function (event) {
        var nextPosition = position;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextPosition = (position + 1) % items.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextPosition = (position - 1 + items.length) % items.length;
        } else if (event.key === "Home") {
          nextPosition = 0;
        } else if (event.key === "End") {
          nextPosition = items.length - 1;
        } else if (event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
          setActiveItem(item.dataset.index);
          return;
        } else {
          return;
        }

        event.preventDefault();
        selectByPosition(nextPosition);
        items[nextPosition].focus();
      });
    });
  }

  function initializeSeries(page) {
    var track = page.querySelector(SERIES_TRACK_SELECTOR);
    var previousButton = page.querySelector(SERIES_PREVIOUS_SELECTOR);
    var nextButton = page.querySelector(SERIES_NEXT_SELECTOR);
    var mobileQuery = getMediaQuery(MOBILE_QUERY);
    var prefersReducedMotion = getMediaQuery(REDUCED_MOTION_QUERY);

    if (!track || (!previousButton && !nextButton)) return function () {};

    function isMobile() {
      return !mobileQuery || mobileQuery.matches;
    }

    function updateControls() {
      var canScroll =
        isMobile() &&
        track.scrollWidth > track.clientWidth + SCROLL_END_TOLERANCE;
      var atStart = track.scrollLeft <= SCROLL_END_TOLERANCE;
      var atEnd =
        track.scrollLeft >=
        track.scrollWidth - track.clientWidth - SCROLL_END_TOLERANCE;

      if (previousButton) {
        previousButton.disabled = !canScroll || atStart;
        previousButton.setAttribute(
          "aria-disabled",
          String(!canScroll || atStart),
        );
      }

      if (nextButton) {
        nextButton.disabled = !canScroll || atEnd;
        nextButton.setAttribute("aria-disabled", String(!canScroll || atEnd));
      }
    }

    function getScrollStep() {
      var cards = track.children;
      if (!cards.length) return track.clientWidth * 0.8;

      var firstCard = cards[0].getBoundingClientRect();
      var secondCard = cards[1] && cards[1].getBoundingClientRect();
      var gap = secondCard ? secondCard.left - firstCard.right : 0;

      return firstCard.width + Math.max(gap, 0);
    }

    function scrollTrack(direction) {
      if (!isMobile()) return;

      var amount = direction * getScrollStep();
      var behavior =
        prefersReducedMotion && prefersReducedMotion.matches
          ? "auto"
          : "smooth";

      if (typeof track.scrollBy === "function") {
        track.scrollBy({ left: amount, behavior: behavior });
      } else {
        track.scrollLeft += amount;
      }
    }

    function handlePreviousClick() {
      scrollTrack(-1);
    }

    function handleNextClick() {
      scrollTrack(1);
    }

    if (previousButton) {
      previousButton.addEventListener("click", handlePreviousClick);
    }

    if (nextButton) {
      nextButton.addEventListener("click", handleNextClick);
    }

    track.addEventListener("scroll", updateControls, { passive: true });
    window.addEventListener("resize", updateControls);
    window.addEventListener("load", updateControls, { once: true });
    var removeMobileListener = addMediaQueryListener(
      mobileQuery,
      updateControls,
    );

    updateControls();

    return function () {
      if (previousButton) {
        previousButton.removeEventListener("click", handlePreviousClick);
      }

      if (nextButton) {
        nextButton.removeEventListener("click", handleNextClick);
      }

      track.removeEventListener("scroll", updateControls);
      window.removeEventListener("resize", updateControls);
      window.removeEventListener("load", updateControls);
      removeMobileListener();
    };
  }

  function initializeWheat(page) {
    var markers = asArray(page.querySelectorAll(WHEAT_MARKER_SELECTOR));

    if (!markers.length) return function () {};

    function setActiveMarker(index) {
      markers.forEach(function (marker) {
        var isActive = marker.dataset.index === index;
        marker.classList.toggle("is-active", isActive);
        marker.setAttribute("aria-pressed", String(isActive));
      });
    }

    function handleMarkerClick(event) {
      var marker = event.currentTarget;
      var nextIndex = marker.classList.contains("is-active")
        ? undefined
        : marker.dataset.index;

      setActiveMarker(nextIndex);
    }

    markers.forEach(function (marker) {
      marker.addEventListener("click", handleMarkerClick);
    });

    return function () {
      markers.forEach(function (marker) {
        marker.removeEventListener("click", handleMarkerClick);
      });
    };
  }

  function initializeFutureCards(page) {
    var cards = asArray(page.querySelectorAll(FUTURE_CARD_SELECTOR));

    if (!cards.length) return;

    function setActiveCard(index) {
      if (typeof index === "undefined") return;

      cards.forEach(function (card) {
        setExpandedState(card, card.dataset.index === index);
      });
    }

    function selectByPosition(position) {
      var card = cards[position];
      if (card) setActiveCard(card.dataset.index);
    }

    var activeCard = cards.filter(function (card) {
      return (
        card.classList.contains("is-active") ||
        Boolean(card.querySelector('[aria-expanded="true"]'))
      );
    })[0];

    setActiveCard((activeCard || cards[0]).dataset.index);

    cards.forEach(function (card, position) {
      card.addEventListener("pointerenter", function (event) {
        if (event.pointerType !== "touch") setActiveCard(card.dataset.index);
      });
      card.addEventListener("focusin", function () {
        setActiveCard(card.dataset.index);
      });
      card.addEventListener("click", function () {
        setActiveCard(card.dataset.index);
      });
      card.addEventListener("keydown", function (event) {
        var nextPosition = position;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextPosition = (position + 1) % cards.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextPosition = (position - 1 + cards.length) % cards.length;
        } else if (event.key === "Home") {
          nextPosition = 0;
        } else if (event.key === "End") {
          nextPosition = cards.length - 1;
        } else if (
          event.key === "Enter" ||
          event.key === " " ||
          event.key === "Spacebar"
        ) {
          event.preventDefault();
          setActiveCard(card.dataset.index);
          return;
        } else {
          return;
        }

        event.preventDefault();
        selectByPosition(nextPosition);
        var nextCard = cards[nextPosition];
        (nextCard.querySelector("button") || nextCard).focus();
      });
    });
  }

  function initializePage(page) {
    if (!page || page.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

    page.setAttribute(INITIALIZED_ATTRIBUTE, "true");
    var cleanupTasks = [
      initializeHero(page),
      initializeWheat(page),
      initializeSeries(page),
    ];

    initializeExplorer(page);
    initializeFutureCards(page);

    page[CLEANUP_PROPERTY] = function () {
      cleanupTasks.forEach(function (cleanup) {
        if (typeof cleanup === "function") cleanup();
      });
    };
  }

  function initializeWithin(root) {
    if (!root) return;

    if (root.matches && root.matches(PAGE_SELECTOR)) initializePage(root);
    asArray(root.querySelectorAll(PAGE_SELECTOR)).forEach(initializePage);
  }

  function destroyWithin(root) {
    if (!root) return;

    var pages = root.matches && root.matches(PAGE_SELECTOR) ? [root] : [];
    pages = pages.concat(asArray(root.querySelectorAll(PAGE_SELECTOR)));

    pages.forEach(function (page) {
      if (typeof page[CLEANUP_PROPERTY] === "function") {
        page[CLEANUP_PROPERTY]();
      }

      delete page[CLEANUP_PROPERTY];
      page.removeAttribute(INITIALIZED_ATTRIBUTE);
    });
  }

  function bindThemeEditorEvents() {
    if (window[EDITOR_EVENTS_GUARD]) return;

    window[EDITOR_EVENTS_GUARD] = true;
    document.addEventListener("shopify:section:load", function (event) {
      initializeWithin(event.target);
    });
    document.addEventListener("shopify:section:unload", function (event) {
      destroyWithin(event.target);
    });
  }

  function initialize() {
    initializeWithin(document);
    bindThemeEditorEvents();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
